import { GraduationCap, X } from "lucide-react";
import { useGetIdentity, useStore, useTranslate } from "ra-core";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { doneCount, missionViews } from "../domain/training/progress";
import { useMyTraining } from "./useTraining";

/**
 * Dashboard card « Formation : X/Y missions » until every mission is done. The rep can hide it (remembered per
 * user in the ra store). Silent while loading or on error: it is a nudge, not a feature.
 */
export const TrainingNudge = () => {
  const t = useTranslate();
  const { identity } = useGetIdentity();
  const [dismissed, setDismissed] = useStore<boolean>(
    `gavion.training.nudge_dismissed.${identity?.id ?? "anon"}`,
    false,
  );
  const q = useMyTraining();
  if (dismissed || !q.data) return null;
  const views = missionViews(q.data);
  const done = doneCount(views);
  const total = views.length;
  if (done >= total) return null;
  const percent = Math.round((done / total) * 100);
  const title = t("gavion.training.nudge.title", { done, total });
  return (
    <section
      aria-label={title}
      className="flex flex-wrap items-center gap-3 rounded-xl border border-primary/30 bg-accent/60 p-3 sm:p-4"
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
        <GraduationCap className="size-5" aria-hidden="true" />
      </span>
      <div className="min-w-48 flex-1 space-y-1.5">
        <p className="text-sm font-bold">{title}</p>
        <div
          role="progressbar"
          aria-label={title}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={done}
          className="h-2 w-full overflow-hidden rounded-full bg-primary/15"
        >
          <div
            className="h-full rounded-full bg-primary"
            style={{ width: `${percent}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {t("gavion.training.nudge.body")}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1 max-sm:w-full max-sm:justify-between">
        <Button asChild size="sm">
          <Link to="/formation">{t("gavion.training.nudge.cta")}</Link>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label={t("gavion.training.nudge.dismiss")}
          onClick={() => setDismissed(true)}
        >
          <X className="size-4" aria-hidden="true" />
        </Button>
      </div>
    </section>
  );
};
